import React, {FunctionComponent, useEffect, useMemo, useRef} from 'react';
import {Box, Chip, CircularProgress, Dialog, Divider, IconButton, InputAdornment, TextField, Typography, useMediaQuery, useTheme,} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import CloseRounded from '@mui/icons-material/CloseRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import DashboardRounded from '@mui/icons-material/DashboardRounded';
import BoltRounded from '@mui/icons-material/BoltRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import {resolveCommandSearchCategoryLayout} from '../category-layout';
import {useCommandSearchShortcutLabel} from '../engine/use-command-shortcut';
import {useHasKeyboardInput} from '../engine/use-has-keyboard-input';
import CommandSearchResultCard from './result-card';
import CommandSearchResultRow from './result-row';
import type {CommandSearchCategoryLayout, CommandSearchGroup, CommandSearchResult} from '../types';

interface CommandSearchDialogProps {
    open: boolean;
    query: string;
    onQueryChange: (value: string) => void;
    onClose: () => void;
    onKeyDown: (event: React.KeyboardEvent) => void;
    /** Filtered groups shown in the results list. */
    groups: CommandSearchGroup[];
    /** Unfiltered groups (including loading) used to build category chips. */
    allGroups: CommandSearchGroup[];
    flatResults: CommandSearchResult[];
    activeIndex: number;
    onActiveIndexChange: (index: number) => void;
    onSelect: (result: CommandSearchResult) => void;
    /** Records a result in Recents without navigating (e.g. Cmd/Ctrl-click or row-action nav). */
    onRememberResult?: (
        result: CommandSearchResult,
        actionKey?: string,
        actionPayload?: Record<string, string | number | boolean | undefined>,
    ) => void;
    selectedCategory: string | null;
    onCategoryToggle: (category: string) => void;
    expandedCategories: Set<string>;
    onGroupExpandToggle: (category: string) => void;
    hasQuery: boolean;
    isSearching?: boolean;
    categoryIcons?: Record<string, React.ReactNode>;
    categoryLayouts?: Record<string, CommandSearchCategoryLayout>;
    searchCategoryLayout?: CommandSearchCategoryLayout;
    placeholder?: string;
    emptyHint?: string;
    /** Pre-formatted "no results" message (e.g. already interpolated with the query). */
    noResultsText?: string;
    /** Accessible label for the fullscreen close control (compact viewports). */
    closeLabel?: string;
    /** Clears all recent entries (shown on the Recent group header). */
    onClearRecents?: () => void;
    /** Removes one recent entry by its display result id (`recent-…`). */
    onRemoveRecent?: (resultId: string) => void;
    /** Accessible label for the Recent group clear control. */
    clearRecentsLabel?: string;
    /** Accessible label for per-item recent remove controls. */
    removeRecentLabel?: string;
}

const DEFAULT_CATEGORY_ICONS: Record<string, React.ReactNode> = {
    recent: <HistoryRounded fontSize="inherit"/>,
    pages: <DashboardRounded fontSize="inherit"/>,
    actions: <BoltRounded fontSize="inherit"/>,
};

const ShortcutKey: FunctionComponent<{ label: string }> = ({label}) => (
    <Box
        component="span"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 22,
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover',
            fontSize: '0.7rem',
            fontWeight: 700,
            lineHeight: 1.2,
            color: 'text.secondary',
        }}
    >
        {label}
    </Box>
);

interface GroupHeaderProps {
    icon?: React.ReactNode;
    label: string;
    count: number;
    loading?: boolean;
    expanded: boolean;
    onToggle: () => void;
    onClear?: () => void;
    clearLabel?: string;
}

const GroupHeader: FunctionComponent<GroupHeaderProps> = ({
                                                              icon,
                                                              label,
                                                              count,
                                                              loading,
                                                              expanded,
                                                              onToggle,
                                                              onClear,
                                                              clearLabel = 'Clear',
                                                          }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 0.75,
            minHeight: 36,
            width: '100%',
            borderRadius: 1,
            '&:hover': {
                bgcolor: 'action.hover',
            },
        }}
    >
        <Box
            component="button"
            type="button"
            onClick={onToggle}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 0.75,
                py: 0.75,
                minHeight: 36,
                flex: 1,
                minWidth: 0,
                border: 0,
                bgcolor: 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
                borderRadius: 1,
            }}
        >
            <Box sx={{color: 'text.secondary', display: 'flex', fontSize: '1.25rem', lineHeight: 0}}>
                {expanded ? <ExpandMoreRounded fontSize="inherit"/> : <ChevronRightRounded fontSize="inherit"/>}
            </Box>
            {icon && (
                <Box sx={{color: 'text.primary', display: 'flex', fontSize: '1rem', lineHeight: 0}}>
                    {icon}
                </Box>
            )}
            <Typography
                variant="caption"
                sx={{
                    color: 'text.primary',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    lineHeight: 1.2,
                }}
            >
                {label}
            </Typography>
            {!loading && count > 0 && (
                <Typography
                    component="span"
                    variant="caption"
                    sx={{
                        color: 'primary.main',
                        fontWeight: 700,
                        lineHeight: 1.2,
                    }}
                >
                    · {count}
                </Typography>
            )}
            {loading && (
                <CircularProgress size={12} thickness={5} sx={{ml: 0.25}}/>
            )}
        </Box>
        {onClear && count > 0 && (
            <IconButton
                size="small"
                aria-label={clearLabel}
                title={clearLabel}
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onClear();
                }}
                sx={{
                    flexShrink: 0,
                    color: 'text.secondary',
                    mr: 0.5,
                    '&:hover': {color: 'error.main'},
                }}
            >
                <CloseRounded fontSize="small"/>
            </IconButton>
        )}
    </Box>
);

/** Command-palette dialog for panel search. */
const CommandSearchDialog: FunctionComponent<CommandSearchDialogProps> = ({
                                                                              open,
                                                                              query,
                                                                              onQueryChange,
                                                                              onClose,
                                                                              onKeyDown,
                                                                              groups,
                                                                              allGroups,
                                                                              flatResults,
                                                                              activeIndex,
                                                                              onActiveIndexChange,
                                                                              onSelect,
                                                                              onRememberResult,
                                                                              selectedCategory,
                                                                              onCategoryToggle,
                                                                              expandedCategories,
                                                                              onGroupExpandToggle,
                                                                              hasQuery,
                                                                              isSearching,
                                                                              categoryIcons,
                                                                              categoryLayouts,
                                                                              searchCategoryLayout,
                                                                              placeholder = 'Search pages and panel actions…',
                                                                              emptyHint = 'Start typing to search panel pages and actions.',
                                                                              noResultsText,
                                                                              closeLabel = 'Close',
                                                                              onClearRecents,
                                                                              onRemoveRecent,
                                                                              clearRecentsLabel = 'Clear recent',
                                                                              removeRecentLabel = 'Remove from recent',
                                                                          }) => {
    const theme = useTheme();
    const isCompact = useMediaQuery(theme.breakpoints.down('sm'));
    const shortcutLabel = useCommandSearchShortcutLabel();
    const hasKeyboardInput = useHasKeyboardInput();
    const inputRef = useRef<HTMLInputElement>(null);
    const itemRefs = useRef<Array<HTMLElement | null>>([]);
    const icons = {...DEFAULT_CATEGORY_ICONS, ...categoryIcons};
    const totalResultCount = useMemo(
        () => groups.reduce((sum, group) => sum + group.items.length, 0),
        [groups],
    );

    const [modifierKey, actionKey] = useMemo(() => {
        if (shortcutLabel.includes('⌘')) {
            return ['⌘', 'K'];
        }
        return ['Ctrl', 'K'];
    }, [shortcutLabel]);

    useEffect(() => {
        if (!open) {
            return;
        }
        const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
        return () => window.clearTimeout(timer);
    }, [open]);

    useEffect(() => {
        itemRefs.current[activeIndex]?.scrollIntoView({block: 'nearest'});
    }, [activeIndex]);

    let runningIndex = -1;

    const renderItem = (item: CommandSearchResult, layout: CommandSearchCategoryLayout) => {
        runningIndex += 1;
        const itemIndex = runningIndex;
        const selected = itemIndex === activeIndex;
        const onRemove = item.category === 'recent' && onRemoveRecent
            ? () => onRemoveRecent(item.id)
            : undefined;
        const commonProps = {
            item,
            selected,
            query,
            itemRef: (element: HTMLElement | null) => {
                itemRefs.current[itemIndex] = element;
            },
            onMouseEnter: () => onActiveIndexChange(itemIndex),
            onClick: () => onSelect(item),
            onRememberResult,
            onRemove,
            removeLabel: removeRecentLabel,
        };

        if (layout.variant === 'grid') {
            const gridSize = layout.gridSize ?? {xs: 12, sm: 6, md: 4};
            return (
                <Grid key={item.id} xs={gridSize.xs} sm={gridSize.sm} md={gridSize.md} lg={gridSize.lg}>
                    <CommandSearchResultCard {...commonProps} />
                </Grid>
            );
        }

        return (
            <CommandSearchResultRow
                key={item.id}
                {...commonProps}
                onClose={onClose}
            />
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            fullScreen={isCompact}
            maxWidth="md"
            BackdropProps={{
                sx: {backdropFilter: 'blur(6px)'},
            }}
            PaperProps={{
                sx: {
                    borderRadius: isCompact ? 0 : 3,
                    overflow: 'hidden',
                    backgroundImage: 'none',
                    border: isCompact ? 0 : 1,
                    borderColor: 'divider',
                    boxShadow: (themeArg) => themeArg.shadows[12],
                    display: 'flex',
                    flexDirection: 'column',
                    ...(isCompact
                        ? {height: '100%'}
                        : {
                            minHeight: 'min(520px, 60dvh)',
                            maxHeight: 'min(1000px, 80dvh)',
                        }),
                },
            }}
        >
            <Box
                onKeyDown={onKeyDown}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                }}
            >
                <Box
                    sx={{
                        px: 2.5,
                        pt: 2,
                        pb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        flexShrink: 0,
                    }}
                >
                    <TextField
                        inputRef={inputRef}
                        value={query}
                        onChange={(event) => onQueryChange(event.target.value)}
                        placeholder={placeholder}
                        fullWidth
                        variant="standard"
                        InputProps={{
                            disableUnderline: true,
                            sx: {
                                fontSize: '1.1rem',
                                py: 0.5,
                                gap: 1,
                            },
                            startAdornment: (
                                <InputAdornment position="start" sx={{mr: 0.5, color: 'text.secondary'}}>
                                    <SearchRounded fontSize="small"/>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {isCompact && (
                        <IconButton
                            onClick={onClose}
                            aria-label={closeLabel}
                            edge="end"
                            sx={{flexShrink: 0, color: 'text.secondary'}}
                        >
                            <CloseRounded/>
                        </IconButton>
                    )}
                </Box>

                {allGroups.length > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 2.5,
                            pb: 1.5,
                            flexWrap: 'wrap',
                            flexShrink: 0,
                        }}
                    >
                        {allGroups.map((group) => {
                            const selected = selectedCategory === group.category;
                            return (
                                <Chip
                                    key={group.category}
                                    size="small"
                                    clickable
                                    color={selected ? 'primary' : 'default'}
                                    variant={selected ? 'filled' : 'outlined'}
                                    icon={icons[group.category]
                                        ? (
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    fontSize: '0.95rem',
                                                    ml: 0.5,
                                                }}
                                            >
                                                {icons[group.category]}
                                            </Box>
                                        )
                                        : undefined}
                                    label={
                                        <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.75}}>
                                            <Box component="span">{group.label}</Box>
                                            {group.loading
                                                ? <CircularProgress size={10} thickness={5} color="inherit"/>
                                                : (
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            fontWeight: 700,
                                                            color: selected ? 'inherit' : 'primary.main',
                                                        }}
                                                    >
                                                        {group.items.length}
                                                    </Box>
                                                )}
                                        </Box>
                                    }
                                    onClick={() => onCategoryToggle(group.category)}
                                    sx={{
                                        maxWidth: '100%',
                                        '& .MuiChip-label': {px: 1},
                                    }}
                                />
                            );
                        })}
                    </Box>
                )}

                <Divider/>

                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        py: 1,
                    }}
                >
                    {!allGroups.length && !hasQuery && (
                        <Box sx={{px: 2.5, py: 4, textAlign: 'center'}}>
                            <Typography color="text.secondary" variant="body2" sx={{mb: 1}}>
                                {emptyHint}
                            </Typography>
                        </Box>
                    )}

                    {!allGroups.length && hasQuery && (
                        <Box sx={{px: 2.5, py: 4, textAlign: 'center'}}>
                            <Typography color="text.secondary" variant="body2">
                                {noResultsText ?? `No results found for "${query.trim()}".`}
                            </Typography>
                        </Box>
                    )}

                    {groups.map((group) => {
                        const layout = resolveCommandSearchCategoryLayout(
                            group.category,
                            hasQuery,
                            categoryLayouts,
                            searchCategoryLayout,
                        );
                        const expanded = expandedCategories.has(group.category);

                        return (
                            <Box key={group.category} sx={{pb: expanded ? 1 : 0}}>
                                <GroupHeader
                                    icon={icons[group.category]}
                                    label={group.label}
                                    count={group.items.length}
                                    loading={group.loading}
                                    expanded={expanded}
                                    onToggle={() => onGroupExpandToggle(group.category)}
                                    onClear={group.category === 'recent' ? onClearRecents : undefined}
                                    clearLabel={clearRecentsLabel}
                                />

                                {expanded && (layout.variant === 'grid' ? (
                                    <Grid container spacing={1} sx={{px: 1.5, m: 0}}>
                                        {group.items.map((item) => renderItem(item, layout))}
                                    </Grid>
                                ) : (
                                    <Box sx={{px: 0.5}}>
                                        {group.items.map((item) => renderItem(item, layout))}
                                    </Box>
                                ))}
                            </Box>
                        );
                    })}
                </Box>

                {!isCompact && (
                    <>
                        <Divider/>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: hasKeyboardInput ? 'space-between' : 'flex-start',
                                gap: 2,
                                px: 2,
                                py: 1.25,
                                bgcolor: 'action.hover',
                                flexWrap: 'wrap',
                                flexShrink: 0,
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                {totalResultCount} result{totalResultCount === 1 ? '' : 's'}
                            </Typography>
                            {hasKeyboardInput && (
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                                        <ShortcutKey label="↑"/>
                                        <ShortcutKey label="↓"/>
                                        <Typography variant="caption" color="text.secondary">
                                            Navigate
                                        </Typography>
                                    </Box>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                                        <ShortcutKey label="↵"/>
                                        <Typography variant="caption" color="text.secondary">
                                            Select
                                        </Typography>
                                    </Box>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                        <ShortcutKey label={modifierKey}/>
                                        <ShortcutKey label={actionKey}/>
                                        <Typography variant="caption" color="text.secondary">
                                            Toggle
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </>
                )}
            </Box>
        </Dialog>
    );
};

export default CommandSearchDialog;
